// frontend/src/hooks/usePerformanceMonitoring.js
import { useEffect, useCallback, useRef } from 'react';

const isDevelopment = process.env.NODE_ENV === 'development';

// No-op function for production
const noOp = () => {};

// Хук для мониторинга производительности React приложения
export const usePerformanceMonitoring = (componentName = 'App') => {
    const renderCount = useRef(0);
    const mountTime = useRef(null);
    const lastRenderTime = useRef(null);

    // Замер времени рендера (ограничиваем частоту)
    const measureRenderTime = useCallback(() => {
        if (!isDevelopment) return;
        const now = performance.now();
        if (lastRenderTime.current) {
            const renderDuration = now - lastRenderTime.current;
            // Логируем только если рендер очень медленный (больше 100ms)
            if (renderDuration > 100) {
                console.warn(`[${componentName}] Slow render: ${renderDuration.toFixed(2)}ms`);
            }
        }
        lastRenderTime.current = now;
        renderCount.current++;
    }, [componentName]);

    // Мониторинг памяти (если доступно)
    const logMemoryUsage = useCallback(() => {
        if (!isDevelopment) return;
        if (performance.memory) {
            const memory = performance.memory;
            console.log(`[${componentName}] Memory usage:`, {
                used: `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
                total: `${(memory.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
                limit: `${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB`
            });
        }
    }, [componentName]);

    // Мониторинг Web Vitals
    const measureWebVitals = useCallback(() => {
        if (!isDevelopment) return;
        // LCP - Largest Contentful Paint
        if ('PerformanceObserver' in window) {
            try {
                const lcpObserver = new PerformanceObserver((entryList) => {
                    const entries = entryList.getEntries();
                    const lastEntry = entries[entries.length - 1];
                    console.log(`[${componentName}] LCP: ${lastEntry.startTime.toFixed(2)}ms`);
                });
                lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

                // FID - First Input Delay
                const fidObserver = new PerformanceObserver((entryList) => {
                    const entries = entryList.getEntries();
                    entries.forEach(entry => {
                        console.log(`[${componentName}] FID: ${entry.processingStart - entry.startTime}ms`);
                    });
                });
                fidObserver.observe({ entryTypes: ['first-input'] });

                return () => {
                    lcpObserver.disconnect();
                    fidObserver.disconnect();
                };
            } catch (error) {
                console.warn('Performance Observer not supported:', error);
            }
        }
    }, [componentName]);

    // Отслеживание долгих задач
    const monitorLongTasks = useCallback(() => {
        if (!isDevelopment) return;
        if ('PerformanceObserver' in window) {
            try {
                const longTaskObserver = new PerformanceObserver((entryList) => {
                    const entries = entryList.getEntries();
                    entries.forEach(entry => {
                        console.warn(`[${componentName}] Long task detected: ${entry.duration.toFixed(2)}ms`);
                    });
                });
                longTaskObserver.observe({ entryTypes: ['longtask'] });
                
                return () => longTaskObserver.disconnect();
            } catch (error) {
                console.warn('Long task monitoring not supported:', error);
            }
        }
    }, [componentName]);

    useEffect(() => {
        if (!isDevelopment) return;
        
        mountTime.current = performance.now();
        console.log(`[${componentName}] Component mounted at ${mountTime.current.toFixed(2)}ms`);
        const cleanupVitals = measureWebVitals();
        const cleanupLongTasks = monitorLongTasks();        
        
        // Периодический мониторинг памяти только в development (каждые 2 минуты)
        const memoryInterval = setInterval(logMemoryUsage, 120000);

        return () => {
            const unmountTime = performance.now();
            const componentLifetime = unmountTime - (mountTime.current || 0);
            // eslint-disable-next-line react-hooks/exhaustive-deps
            const totalRenders = renderCount.current; // Копируем значение для cleanup функции
            console.log(`[${componentName}] Component unmounted after ${componentLifetime.toFixed(2)}ms, total renders: ${totalRenders}`);
            
            clearInterval(memoryInterval);
            cleanupVitals?.();
            cleanupLongTasks?.();
        };
    }, [componentName, measureWebVitals, monitorLongTasks, logMemoryUsage]);

    // Вызывать на каждом рендере
    useEffect(() => {
        if (!isDevelopment) return;
        measureRenderTime();
    });

    return {
        renderCount: renderCount.current,
        logMemoryUsage: isDevelopment ? logMemoryUsage : noOp,
        measureRenderTime: isDevelopment ? measureRenderTime : noOp,
    };
};

// Хук для мониторинга конкретных операций
export const useOperationTiming = () => {
    const timers = useRef(new Map());

    const startTimer = useCallback((operationName) => {
        if (!isDevelopment) return;
        timers.current.set(operationName, performance.now());
    }, []);

    const endTimer = useCallback((operationName) => {
        if (!isDevelopment) return null;
        const startTime = timers.current.get(operationName);
        if (startTime) {
            const duration = performance.now() - startTime;
            console.log(`Operation "${operationName}" took ${duration.toFixed(2)}ms`);
            timers.current.delete(operationName);
            return duration;
        }
        return null;
    }, []);

    return { 
        startTimer: isDevelopment ? startTimer : noOp,
        endTimer: isDevelopment ? endTimer : () => null,
    };
};

// Хук для мониторинга сетевых запросов
export const useNetworkMonitoring = () => {
    useEffect(() => {
        if (!isDevelopment) return;

        const originalFetch = window.fetch;
        
        window.fetch = async (...args) => {
            const startTime = performance.now();
            const url = args[0];
            
            try {
                const response = await originalFetch(...args);
                const duration = performance.now() - startTime;
                
                console.log(`Network request to ${url} took ${duration.toFixed(2)}ms`, {
                    status: response.status,
                    ok: response.ok
                });
                
                return response;
            } catch (error) {
                const duration = performance.now() - startTime;
                console.error(`Network request to ${url} failed after ${duration.toFixed(2)}ms`, error);
                throw error;
            }
        };

        return () => {
            window.fetch = originalFetch;
        };
    }, []);
};
